import React, { useEffect, useState } from "react"
import _get from "lodash/get"
import { useTranslation } from "next-i18next"

import Modal from "@/components/core/Modal/Modal"
import ReCaptcha from "@/components/core/ReCaptcha/ReCaptcha"

import { getConfig } from "@/constants/config"
import { apim } from "@/constants/api"
import {
  DEFAULT_NEWELETTER_POPUP_TRIGGER_TIME,
  PRIVACY_COOKIE_CLICKED,
  customActivityEvents,
} from "@/constants/index"
import { sanitizeInnerHtml, preventBodyScroll } from "@/utils/helper"
import style from "@/components/NewsLetterSignUpOverlay/v1/index.module.scss"
import SuccessMessage from "@/components/NewsLetterSignUpOverlay/v1/SuccessMessage"
import NewsLetterForm from "@/components/NewsLetterSignUpOverlay/v1/NewsLetterForm"
import ActivityDetector from "react-activity-detector"
import {
  addAnalyticsForNewsletterSignupBtn,
  addAnalyticsForNewsletterSignupLinks,
  getRecaptchaAnalytics,
} from "@/components/NewsLetterSignUpOverlay/v1/analytics"

const NewsLetterSignUpOverlay = ({
  data,
  showCampaign = false,
  handleModalClose,
}) => {
  const { t } = useTranslation("common")
  const staticContent = data?.content || {}
  const {
    exceptionalPage = [],
    useTimer = 0,
    copyCtaText = "",
    copiedCtaText = "",
    promoCode = "",
    usedForCampaign = false,
    formTitle = "",
    formDescription = "",
    confirmationTitle = "",
    confirmationDescription = "",
    ctaLabel = "",
    ctaCheckboxParameters = [],
    apiEventType = "",
    endpointUrl = "",
    bodyParameters = {},
  } = staticContent

  const i18n = {
    privacyPolicy: t("kf.associatemembership.label.privacyPolicy"),
    subjectAccessRequest: t(
      "kf.associatemembership.label.subjectAccessRequest"
    ),
    termsOfService: t("kf.associatemembership.label.termsOfService"),
  }
  const [config, setConfig] = useState({})
  const [email, setEmail] = useState("")
  const [showModal, setShowModal] = useState(showCampaign)
  const [idealTime, setIdealTime] = useState(
    DEFAULT_NEWELETTER_POPUP_TRIGGER_TIME
  )

  const [heritageCheckbox, setHeritageCheckbox] = useState(
    ctaCheckboxParameters.reduce(
      (obj, item) => Object.assign(obj, { [item.checkboxLabel]: true }),
      {}
    )
  )

  const [state, setState] = useState({
    cookieval: true,
    overlayError: "",
    showSuccessModal: false,
    confirmModal: false,
    cookieAccepted: false,
    timeExceeded: false,
  })

  useEffect(() => {
    getConfig().then(CONFIG => {
      setConfig({
        siteName: _get(CONFIG, "general.siteName", ""),
        legalPages: _get(CONFIG, "legalPageData.map", {}),
      })
    })
    if (usedForCampaign) localStorage.removeItem("isSignedUpForNewsletter")
  }, [])

  useEffect(() => {
    if (useTimer) {
      setShowModal(true)
      // on page load open the popup based on the timer
      const timer = Number(
        staticContent?.timer || DEFAULT_NEWELETTER_POPUP_TRIGGER_TIME
      )
      setIdealTime(timer)
    }
  }, [staticContent])

  useEffect(() => {
    if (showCampaign) setShowModal(showCampaign)
  }, [showCampaign])

  const onFormSubmit = event => {
    event.preventDefault()
    const getEmailval = email
    if (getEmailval === "") {
      setState({ ...state, overlayError: "Email Address*" })
    } else if (!/\S+@\S+\.\S+/.test(getEmailval)) {
      setEmail("")
      setState({ ...state, overlayError: "Enter valid Email Address" })
    } else {
      getSubscriptionData()
    }
  }

  const closeModal = () => {
    handleNewseletterModalClose()
  }

  const getSubscriptionData = () => {
    if (email) {
      let params = {
        brand: config.siteName.toLowerCase(),
      }

      let body = {
        keys: {
          SubscriberKey: email,
        },
        values: { ...bodyParameters },
      }
      body.values["EmailAddress"] = email
      body.values["Date Updated"] = new Date().toISOString()

      if (usedForCampaign) {
        const heritageCheckboxPayload = {}
        if (ctaCheckboxParameters.length) {
          const heritageCheckboxKeys = Object.keys(heritageCheckbox).filter(
            item => heritageCheckbox[item]
          )
          const heritageCheckboxes = ctaCheckboxParameters.filter(item =>
            heritageCheckboxKeys.includes(item.checkboxLabel)
          )
          heritageCheckboxes.forEach(item => {
            const heritageCheckboxKeyObj = item.checkboxParameters.reduce(
              (obj, itemObj) =>
                Object.assign(obj, {
                  [itemObj.checkboxKey]: itemObj.checkboxValue,
                }),
              {}
            )
            Object.keys(heritageCheckboxKeyObj).forEach(key => {
              heritageCheckboxPayload[key] = heritageCheckboxKeyObj[key]
            })
          })
        }

        body = {
          email,
          ...heritageCheckboxPayload,
        }
        params = {
          event: apiEventType,
        }
      }

      const requestPayload = usedForCampaign ? body : [body]

      apim
        .post(endpointUrl, requestPayload, {
          params,
        })
        .then(
          response => {
            if (response && response.data) {
              setState({
                ...state,
                confirmModal: true,
              })
              if (!usedForCampaign)
                localStorage.setItem("isSignedUpForNewsletter", "true")

              addAnalyticsForNewsletterSignupBtn("success")
            } else {
              addAnalyticsForNewsletterSignupBtn("failure", email)
              console.error(
                "News letter subscription API: Error occured while fetching data from Get URL Property"
              )
            }
          },
          errObj => {
            const {
              data: { message },
            } = errObj.response
            const {
              data: { error },
            } = errObj.response
            addAnalyticsForNewsletterSignupBtn("failure", email)
            setState({
              ...state,
              overlayError: message || error,
            })
          }
        )
    }
  }

  useEffect(() => {
    if (state.cookieAccepted) {
      localStorage.setItem(PRIVACY_COOKIE_CLICKED, state.cookieAccepted)
    }
  }, [state.cookieAccepted])

  const handleNewseletterModalClose = () => {
    setShowModal(false)
    setState({ ...state, confirmModal: false })
    if (useTimer) localStorage.setItem("isSignedUpForNewsletter", "true")
    handleModalClose()
    preventBodyScroll(false)
    addAnalyticsForNewsletterSignupLinks("close", "n/a")
  }

  useEffect(() => {
    if (
      !isDataEditorMode &&
      !isSignedUpForNewsletter &&
      timeExceeded &&
      isDisplay === false
    ) {
      preventBodyScroll(true)
    }
  }, [state.timeExceeded])

  const onIdle = () => {
    setState({ ...state, timeExceeded: true })
  }

  const { confirmModal, timeExceeded, overlayError } = state
  const isDisplay =
    exceptionalPage &&
    exceptionalPage.length &&
    exceptionalPage.some(
      page => page.exceptionalPageName === window.location.pathname
    )
  const isSubscribe = !!localStorage.getItem("isSignedUpForNewsletter")
  const isSignedUpForNewsletter = isSubscribe && !confirmModal
  const isDataEditorMode = document.body.hasAttribute("data-editor-mode")
  const isModalVisibleBasedOnTimer =
    !isDataEditorMode &&
    !isSignedUpForNewsletter &&
    timeExceeded &&
    isDisplay === false

  const showNewsletterModal = useTimer
    ? isModalVisibleBasedOnTimer
    : !isSignedUpForNewsletter && showModal

  config.legalPages?.privacyPolicy
  const {
    legalPages: {
      privacyPolicy = "n/a",
      subjectAccessRequest = "n/a",
      termsOfService = "n/a",
    } = {},
  } = config
  const {
    privacyPolicy: privacyPolicyText = "",
    subjectAccessRequest: subjectAccessRequestText = "",
    termsOfService: termsOfServiceText = "",
  } = i18n
  const handleClickOfLinks = (text, url) => {
    window.open(url)
    addAnalyticsForNewsletterSignupLinks(text, url)
  }

  return (
    <div className={style.signupModalwrapper}>
      {useTimer ? (
        <ActivityDetector
          activityEvents={customActivityEvents}
          enabled={!isSignedUpForNewsletter}
          timeout={idealTime * 1000}
          onIdle={onIdle}
          name="newsletterSignupModalActivityDetector"
        />
      ) : null}
      <div className="news-letter-sign-up">
        {showNewsletterModal ? (
          <Modal
            showModal={showModal}
            onModalClose={handleNewseletterModalClose}
          >
            <div className="news-letter-sign-up__newsletter-overlay">
              {!confirmModal && (
                <div className="news-letter-sign-up__content">
                  <div className="news-letter-sign-up__header">
                    {sanitizeInnerHtml(formTitle)}
                  </div>
                  <div className="news-letter-sign-up__subcopy">
                    {sanitizeInnerHtml(formDescription)}
                  </div>
                  <NewsLetterForm
                    setEmail={setEmail}
                    overlayError={overlayError}
                    onFormSubmit={onFormSubmit}
                    ctaLabel={ctaLabel}
                    heritageCheckbox={heritageCheckbox}
                    setHeritageCheckbox={setHeritageCheckbox}
                  />
                  <ReCaptcha
                    getAnalyticsData={getRecaptchaAnalytics}
                    isPopupNeededBeforeRedirection={false}
                  />
                  <div className="news-letter-sign-up__policies">
                    <a
                      href="#"
                      onClick={() =>
                        handleClickOfLinks(privacyPolicyText, privacyPolicy)
                      }
                      role="link"
                    >
                      {privacyPolicyText}
                    </a>
                    <a
                      href="#"
                      onClick={() =>
                        handleClickOfLinks(
                          subjectAccessRequestText,
                          subjectAccessRequest
                        )
                      }
                    >
                      {subjectAccessRequestText}
                    </a>
                    <a
                      href="#"
                      onClick={() =>
                        handleClickOfLinks(termsOfServiceText, termsOfService)
                      }
                    >
                      {termsOfServiceText}
                    </a>
                  </div>
                </div>
              )}

              {confirmModal && (
                <SuccessMessage
                  confirmationTitle={confirmationTitle}
                  confirmationDescription={confirmationDescription}
                  closeModal={closeModal}
                  promoCode={promoCode}
                  copyCtaText={copyCtaText}
                  copiedCtaText={copiedCtaText}
                  isGiftCodeEnable={usedForCampaign}
                />
              )}
            </div>
          </Modal>
        ) : null}
      </div>
    </div>
  )
}

export default NewsLetterSignUpOverlay
